import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {StandardCodeOptionDto} from '../../../../models/generated-models';

export {StandardCodeOptionDto} from '../../../../models/generated-models';

@Component({
  selector: 'app-standard-code-autocomplete',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './standard-code-autocomplete.component.html',
  styleUrl: './standard-code-autocomplete.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StandardCodeAutocompleteComponent),
      multi: true
    }
  ]
})
export class StandardCodeAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() id = 'standard-code';
  @Input() name = 'standard-code';
  @Input() placeholder = 'พิมพ์ค้นหารหัสหรือชื่อมาตรฐาน';
  @Input() size: 'sm' | 'md' = 'md';
  @Input() bindValue?: keyof StandardCodeOptionDto | null = 'title';
  @Input() disabled = false;
  @Input() allowCustom = true;

  @Output() readonly standardSelected = new EventEmitter<StandardCodeOptionDto | null>();

  @ViewChild('searchInput', {static: true}) searchInput?: ElementRef<HTMLInputElement>;

  selectedItem: StandardCodeOptionDto | null = null;
  searchText = '';
  suggestions: StandardCodeOptionDto[] = [];
  loading = false;
  open = false;
  highlightedIndex = -1;
  readonly standaloneOptions = {standalone: true};

  private selectedValue: StandardCodeOptionDto | number | string | null = null;
  private requestSequence = 0;
  private searchSubject$ = new Subject<string>();
  private searchSub?: Subscription;

  private onChange: (value: StandardCodeOptionDto | number | string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly http = inject(HttpClient);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.searchSub = this.searchSubject$.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((term) => {
      this.executeSearch(term);
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  writeValue(value: StandardCodeOptionDto | number | string | null): void {
    this.selectedValue = value;

    if (this.selectedValue == null || this.selectedValue === '') {
      this.selectedItem = null;
      this.searchText = '';
      this.suggestions = [];
      this.cdr.markForCheck();
      return;
    }

    if (typeof this.selectedValue === 'object') {
      this.selectedItem = this.selectedValue;
      this.searchText = this.formatStandardCode(this.selectedValue);
      this.cdr.markForCheck();
      return;
    }

    const numId = Number(this.selectedValue);
    if (this.bindValue === 'id' && !isNaN(numId) && numId > 0) {
      const found = this.suggestions.find((s) => s.id === numId);
      if (found) {
        this.selectedItem = found;
        this.searchText = this.formatStandardCode(found);
        this.cdr.markForCheck();
        return;
      }

      this.http.get<StandardCodeOptionDto>(`${this.standardCodeApiUrl()}/${numId}`).subscribe({
        next: (item) => {
          this.selectedItem = item;
          this.searchText = this.formatStandardCode(item);
          this.cdr.markForCheck();
        },
        error: () => {
          this.selectedItem = null;
          this.searchText = `${this.selectedValue}`;
          this.cdr.markForCheck();
        }
      });
      return;
    }

    const strVal = `${this.selectedValue}`.trim();
    this.searchText = strVal;
    if (this.selectedItem && (this.selectedItem.title === strVal || this.formatCodeDisplay(this.selectedItem) === strVal)) {
      this.cdr.markForCheck();
      return;
    }

    const found = this.suggestions.find((s) => s.title === strVal || this.formatCodeDisplay(s) === strVal);
    if (found) {
      this.selectedItem = found;
      this.cdr.markForCheck();
      return;
    }

    this.selectedItem = null;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: StandardCodeOptionDto | number | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.highlightedIndex = -1;
      this.onTouched();
      this.cdr.markForCheck();
    }
  }

  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target === this.host.nativeElement) {
      if (this.selectedItem && !this.open) {
        this.onSelectedClick();
      } else {
        this.searchInput?.nativeElement.focus();
      }
    }
  }

  onSelectedClick(): void {
    if (this.disabled) return;
    this.open = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      this.searchInput?.nativeElement.select();
    });
    if (this.suggestions.length === 0) {
      this.loadInitialSuggestions();
    }
  }

  onInputChange(): void {
    const term = this.searchText.trim();
    if (this.selectedItem && this.formatStandardCode(this.selectedItem) !== term) {
      this.selectedItem = null;
    }
    if (this.bindValue === 'title' && this.allowCustom) {
      this.selectedValue = term || null;
      this.onChange(term || null);
    } else {
      this.selectedValue = null;
      this.onChange(null);
    }
    this.highlightedIndex = -1;

    if (!term) {
      this.suggestions = [];
      this.open = false;
      this.loading = false;
      this.standardSelected.emit(null);
      return;
    }

    this.loading = true;
    this.searchSubject$.next(term);
  }

  onFocus(): void {
    if (this.disabled) return;
    if (this.suggestions.length > 0) {
      this.open = true;
    } else if (!this.searchText.trim()) {
      this.loadInitialSuggestions();
    } else {
      this.executeSearch(this.searchText.trim());
    }
  }

  handleBlur(): void {
    // Let onClickOutside manage closing on clicks outside so item selection isn't interrupted
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open) {
          this.openDropdown();
        } else if (this.suggestions.length > 0) {
          this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions.length;
          this.scrollHighlightedIntoView();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.open && this.suggestions.length > 0) {
          this.highlightedIndex = (this.highlightedIndex - 1 + this.suggestions.length) % this.suggestions.length;
          this.scrollHighlightedIntoView();
        }
        break;

      case 'Enter':
        if (this.open && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
          event.preventDefault();
          this.selectStandard(this.suggestions[this.highlightedIndex]);
        }
        break;

      case 'Escape':
        if (this.open) {
          event.preventDefault();
          event.stopPropagation();
          this.open = false;
          this.highlightedIndex = -1;
        }
        break;

      case 'Tab':
        this.open = false;
        this.highlightedIndex = -1;
        break;
    }
  }

  selectStandard(item: StandardCodeOptionDto, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedItem = item;
    this.selectedValue = item;
    this.searchText = this.formatStandardCode(item);
    this.suggestions = [];
    this.open = false;
    this.highlightedIndex = -1;

    let valueToEmit: StandardCodeOptionDto | number | string | null = null;
    if (this.bindValue === null || this.bindValue === undefined) {
      valueToEmit = item;
    } else if (this.bindValue === 'id') {
      valueToEmit = item.id ?? null;
    } else if (this.bindValue === 'title') {
      valueToEmit = item.title ?? null;
    } else {
      valueToEmit = (item as any)[this.bindValue] ?? null;
    }

    this.onChange(valueToEmit);
    this.onTouched();
    this.standardSelected.emit(item);
    this.cdr.markForCheck();
  }

  clear(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.disabled) return;

    this.searchText = '';
    this.selectedItem = null;
    this.selectedValue = null;
    this.suggestions = [];
    this.open = false;
    this.highlightedIndex = -1;
    this.onChange(null);
    this.onTouched();
    this.standardSelected.emit(null);
    this.cdr.markForCheck();
    this.searchInput?.nativeElement.focus();
  }

  toggleOpen(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.disabled) return;

    if (this.open) {
      this.open = false;
      this.highlightedIndex = -1;
    } else {
      this.open = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
        this.searchInput?.nativeElement.select();
      });
      if (this.suggestions.length === 0) {
        this.loadInitialSuggestions();
      }
    }
    this.cdr.markForCheck();
  }

  isSelected(item: StandardCodeOptionDto): boolean {
    if (this.selectedItem?.id != null && item.id != null) {
      return this.selectedItem.id === item.id;
    }
    if (this.selectedValue == null) return false;
    if (typeof this.selectedValue === 'object') {
      return this.selectedValue.id != null && this.selectedValue.id === item.id;
    }
    if (this.bindValue === 'id') {
      return item.id != null && Number(this.selectedValue) === item.id;
    }
    if (this.bindValue === 'title') {
      return this.selectedValue === item.title;
    }
    return false;
  }

  formatCodeDisplay(item: StandardCodeOptionDto): string {
    const pre = (item?.preCode || 'มกษ.').trim();
    const code = item?.standardCode?.trim();
    const short = (item?.shortStandard || item?.shortStandardCode)?.trim();

    const parts: string[] = [];
    if (code) {
      parts.push(`${pre} ${code}`);
    } else if (pre) {
      parts.push(pre);
    }
    if (short) {
      parts.push(short);
    }
    return parts.join(' ').trim();
  }

  private openDropdown(): void {
    this.searchInput?.nativeElement.focus();
    if (this.searchText.trim() && this.selectedValue == null) {
      this.executeSearch(this.searchText.trim());
    } else {
      this.loadInitialSuggestions();
    }
  }

  private loadInitialSuggestions(): void {
    const currentSeq = ++this.requestSequence;
    this.loading = true;
    this.http.get<StandardCodeOptionDto[] | { content?: StandardCodeOptionDto[] }>(this.standardCodeApiUrl()).subscribe({
      next: (res) => {
        if (currentSeq !== this.requestSequence) return;
        const items = Array.isArray(res) ? res : res?.content ?? [];
        this.suggestions = items;
        this.open = true;
        this.highlightedIndex = -1;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (currentSeq !== this.requestSequence) return;
        this.suggestions = [];
        this.open = false;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private executeSearch(term: string): void {
    const currentSeq = ++this.requestSequence;
    this.loading = true;
    this.http.get<StandardCodeOptionDto[] | { content?: StandardCodeOptionDto[] }>(
      `${this.standardCodeApiUrl()}?search=${encodeURIComponent(term)}`
    ).subscribe({
      next: (res) => {
        if (currentSeq !== this.requestSequence) return;
        const items = Array.isArray(res) ? res : res?.content ?? [];
        this.suggestions = items;
        this.open = true;
        this.highlightedIndex = -1;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (currentSeq !== this.requestSequence) return;
        this.suggestions = [];
        this.open = false;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private scrollHighlightedIntoView(): void {
    setTimeout(() => {
      const items = this.host.nativeElement.querySelectorAll('.dropdown-item');
      if (this.highlightedIndex >= 0 && this.highlightedIndex < items.length) {
        (items[this.highlightedIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    });
  }

  private standardCodeApiUrl(): string {
    const serverUrl = (environment as any).serverUrl ? (environment as any).serverUrl.replace(/\/+$/, '') : '';
    return `${serverUrl}/api/master/standard-codes`;
  }

  private formatStandardCode(item: StandardCodeOptionDto): string {
    return `${item?.title ?? ''}`.trim();
  }
}
